import { Box, Image, Spinner, Text } from '@chakra-ui/react'
import React from 'react'
import { getCachedAssetImage, loadAssetImage } from '../../core/assetImageCache'

interface AssetImageProps {
  src: string
  alt?: string
}

// Renders an inline document asset image. The assets endpoint is Bearer-authed,
// so a plain `<img src>` gets a 401 — the browser cannot attach the token.
// Bytes are fetched through the authed API client and rendered from an in-memory
// object URL, cached by `src` (see assetImageCache). The cache is warmed on the
// `images` SSE event, so by the time the `[IMG#]` marker resolves and this
// component mounts, the blob is usually ready — no spinner, and remounts caused
// by re-parsing the streaming markdown hit the cache instead of refetching.
const AssetImage: React.FC<AssetImageProps> = ({ src, alt }) => {
  // Seed from the cache so a warm remount renders the image immediately.
  const [objectUrl, setObjectUrl] = React.useState<string | null>(() => getCachedAssetImage(src))
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    const cached = getCachedAssetImage(src)
    setObjectUrl(cached)
    setFailed(false)
    if (cached) return

    loadAssetImage(src)
      .then(url => {
        if (!cancelled) setObjectUrl(url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [src])

  if (failed) {
    return (
      <Text as='span' color='gray.500' fontSize='sm' fontStyle='italic'>
        {alt ? `[image: ${alt}]` : '[image unavailable]'}
      </Text>
    )
  }

  if (!objectUrl) {
    // Reserve vertical space while the blob loads so the image doesn't pop in
    // and shove the surrounding text down when it resolves. Intrinsic size is
    // unknown until decode, so this is a best-effort minimum, not exact.
    return (
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        minH='120px'
        my={2}
      >
        <Spinner size='sm' color='gray.400' />
      </Box>
    )
  }

  return <Image src={objectUrl} alt={alt ?? ''} maxW='100%' borderRadius='md' my={2} />
}

export default AssetImage
